import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const STATUS_COLORS = { pending:'badge-gray', confirmed:'badge-navy', processing:'badge-navy', shipped:'badge-green', delivered:'badge-green', cancelled:'badge-red' };

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fmt = n => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 4 }}>Dashboard</h1>
        <p style={{ color: '#9a9a9a', fontSize: 14 }}>Premier Pawn & Tech Repair — Admin Overview</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {[
          { label: "Today's Revenue", value: fmt(data?.todayRevenue?.total), sub: `${data?.todayRevenue?.count} orders`, color: '#2e7d32' },
          { label: 'Monthly Revenue',  value: fmt(data?.monthRevenue?.total),  sub: `${data?.monthRevenue?.count} orders`, color: '#1a2744' },
          { label: 'Pending Orders',   value: data?.pendingOrders,             sub: 'Awaiting processing', color: '#e65100' },
          { label: 'Items Available',  value: data?.totalProducts,             sub: 'In stock', color: '#1976d2' },
          { label: 'New Inquiries',    value: data?.newInquiries,              sub: 'Pawn/sell requests', color: '#c9a227' },
        ].map(stat => (
          <div key={stat.label} style={{ background: '#fff', borderRadius: 12, padding: '20px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: 12, color: '#9a9a9a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{stat.label}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: stat.color, marginBottom: 4 }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: '#bbb' }}>{stat.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Recent orders */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', fontSize: 15 }}>Recent Orders</h3>
            <Link to="/admin/orders" style={{ fontSize: 12, color: '#c9a227', fontWeight: 700 }}>View All →</Link>
          </div>
          {!data?.recentOrders?.length ? (
            <div style={{ color: '#bbb', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No orders yet</div>
          ) : data.recentOrders.map(order => (
            <div key={order.order_number} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f4f4f4' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2744', fontFamily: 'monospace' }}>{order.order_number}</div>
                <div style={{ fontSize: 11, color: '#9a9a9a', marginTop: 1 }}>{order.customer || 'Guest'} · {new Date(order.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className={`badge ${STATUS_COLORS[order.status] || 'badge-gray'}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{order.status}</span>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1a2744' }}>${parseFloat(order.total).toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Top categories */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', fontSize: 15 }}>Top Categories</h3>
            <Link to="/admin/inventory" style={{ fontSize: 12, color: '#c9a227', fontWeight: 700 }}>Inventory →</Link>
          </div>
          {!data?.topCategories?.length ? (
            <div style={{ color: '#bbb', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No sales data yet</div>
          ) : data.topCategories.map(cat => (
            <div key={cat.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f4f4f4', fontSize: 13 }}>
              <span style={{ fontWeight: 600, color: '#444' }}>{cat.name}</span>
              <span style={{ fontWeight: 700, color: '#1a2744' }}>{fmt(cat.revenue)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 20 }}>
        <h3 style={{ fontWeight: 700, color: '#1a2744', fontSize: 15, marginBottom: 14 }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/admin/inventory" className="btn btn-navy btn-sm">+ Add Item</Link>
          <Link to="/admin/import" className="btn btn-outline btn-sm">📥 Import CSV</Link>
          <Link to="/admin/orders" className="btn btn-outline btn-sm">🛒 View Orders</Link>
          <Link to="/admin/inquiries" className="btn btn-outline btn-sm">💬 View Inquiries</Link>
          <Link to="/admin/settings" className="btn btn-outline btn-sm">⚙️ Settings</Link>
        </div>
      </div>
    </div>
  );
}
